import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RevuesPressService } from '../revues-presse.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

interface Revue {
  id_press_article: number;
  title_ln1: string;
  source_ln1?: string;
  source_logo_url?: string;
  publish_date?: string;
  article_url?: string;
  snippet?: string;
  status?: 'ACTI' | 'INAC';
  status_label?: string;
  creation_date?: string;
  updated_date?: string;
  press_tags?: string;
}

@Component({
  selector: 'app-revues-detail',
  standalone: false,
  templateUrl: './revues-detail.component.html',
  styleUrls: ['./revues-detail.component.scss'],
})
export class RevuesDetailComponent implements OnInit, OnDestroy {
  private route  = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(RevuesPressService);
  private destroy$ = new Subject<void>();

  revue       = signal<Revue | null>(null);
  isLoading   = signal(true);
  errorMessage = signal<string | null>(null);

  pendingStatus = signal<{ status: 'ACTI' | 'INAC' } | null>(null);
  pendingDelete = signal(false);
  isSaving      = signal(false);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.service.getRevueById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => { this.revue.set(res.data || res); this.isLoading.set(false); },
      error: () => { this.errorMessage.set('Impossible de charger cet article'); this.isLoading.set(false); },
    });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  goBack(): void { this.router.navigate(['/federation/revues-presse']); }
  onEdit(): void { this.router.navigate(['/federation/revues-presse', this.revue()!.id_press_article, 'edit']); }

  confirmStatus(status: 'ACTI' | 'INAC'): void { this.pendingStatus.set({ status }); }
  cancelStatus(): void { this.pendingStatus.set(null); }

  applyStatus(): void {
    const r = this.revue();
    const pending = this.pendingStatus();
    if (!r || !pending) return;
    this.isSaving.set(true);
    this.pendingStatus.set(null);
    this.service.setStatus(r.id_press_article, pending.status)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.revue.update(prev => prev ? { ...prev, status: pending.status } : prev);
          this.isSaving.set(false);
          const label = pending.status === 'ACTI' ? 'En ligne' : 'Hors ligne';
          SwalUtils.success('Statut mis à jour', `La revue est maintenant ${label}.`);
        },
        error: () => { SwalUtils.error('Erreur', 'Impossible de mettre à jour le statut.'); this.isSaving.set(false); },
      });
  }

  confirmDelete(): void { this.pendingDelete.set(true); }
  cancelDelete(): void { this.pendingDelete.set(false); }

  applyDelete(): void {
    const r = this.revue();
    if (!r) return;
    this.isSaving.set(true);
    this.pendingDelete.set(false);
    this.service.setStatus(r.id_press_article, 'ARCH').pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.router.navigate(['/federation/revues-presse']),
      error: () => { SwalUtils.error('Erreur', 'Impossible d\'archiver cet article.'); this.isSaving.set(false); },
    });
  }

  statusLabel(status: string): string {
    return ({ ACTI: 'En ligne', INAC: 'Hors ligne' } as any)[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  pendingStatusLabel(status: 'ACTI' | 'INAC'): string {
    return ({ ACTI: 'Publier', INAC: 'Dépublier' } as any)[status];
  }

  articleDomain(): string {
    try { return new URL(this.revue()!.article_url!).hostname; } catch { return ''; }
  }

  onFaviconError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}
